import { Button, Label, StringUtils, Switch, TextField } from '@localina/core';
import { EmailIcon, LockIcon } from '@localina/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    useGetAuthUser,
    useLoginMutation,
    useLogoutMutation,
    useSendEmailVerificationMutation,
} from '../../api/queries/auth';
import { useGetRestaurants } from '../../api/queries/restaurants';
import { useRevokeAccountAccess } from '../../api/queries/supportAccounts';
import { AuthLayout, PasswordToggle } from '../../components';
import { Path } from '../../enums';
import { IAuthCredentials } from '../../interfaces';
import { PathUtils, RoleUtils, SupportUtils } from '../../utils';
import { queryClient } from '../App';

type TFieldErrors = { email?: string; password?: string };

const validateCredentials = (credentials: IAuthCredentials): TFieldErrors => ({
    email: !credentials.email
        ? 'auth.errors.emailRequired'
        : !StringUtils.isValidEmail(credentials.email)
        ? 'auth.errors.emailInvalid'
        : undefined,
    password: !credentials.password ? 'auth.errors.passwordRequired' : undefined,
});

const Login: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const revokeAccountAccessMutation = useRevokeAccountAccess();
    const loginMutation = useLoginMutation();
    const logoutMutation = useLogoutMutation({
        onSuccess: () => {
            return authUserQuery.refetch();
        },
    });

    const [credentials, setCredentials] = React.useState<IAuthCredentials>({
        email: '',
        password: '',
        keepSignedIn: true,
    });
    const [fieldErrors, setFieldErrors] = React.useState<TFieldErrors>({});
    const [showPassword, setShowPassword] = React.useState(false);
    const authUserQuery = useGetAuthUser({
        enabled: loginMutation.isSuccess,
    });
    const restaurantsQuery = useGetRestaurants({
        enabled: authUserQuery.isSuccess && !authUserQuery.data.user,
    });

    const hasNoRestaurants =
        authUserQuery.isSuccess && restaurantsQuery.isSuccess && restaurantsQuery.data.restaurants.length === 0;

    const handleRevoke = () => {
        revokeAccountAccessMutation.mutate(undefined, {
            onSuccess: () => {
                navigate(Path.SUPPORT_ACCOUNTS);
                SupportUtils.resetSession();
                window.location.reload();
            },
        });
    };

    React.useEffect(() => {
        if (authUserQuery.isSuccess && authUserQuery.data.isLoggedIn) {
            if (authUserQuery.data.user && (authUserQuery.data.hasSupportRole || authUserQuery.data.hasSalesRole)) {
                if (!authUserQuery.data.accountId) {
                    navigate(Path.SUPPORT_ACCOUNTS);
                }
            }
        }
    }, [authUserQuery.isSuccess]);

    React.useEffect(() => {
        if (authUserQuery.isSuccess && authUserQuery.data.isLoggedIn) {
            if (restaurantsQuery.isSuccess && restaurantsQuery.data.restaurants.length) {
                const restaurantId = restaurantsQuery.data.restaurants[0].id;
                navigate(PathUtils.generateUrl(Path.RESTAURANT_DASHBOARD, { restaurantId }));
            } else if (
                restaurantsQuery.isSuccess &&
                !restaurantsQuery.data.restaurants.length &&
                (authUserQuery.data.hasSupportRole || authUserQuery.data.hasSalesRole)
            ) {
                handleRevoke();
            } else if (restaurantsQuery.isError) {
                logoutMutation.mutate();
            }
        }
    }, [authUserQuery.status, restaurantsQuery.status]);

    const handleEmailChange = (value: string) => {
        setCredentials({ ...credentials, email: value });
        setFieldErrors({ ...fieldErrors, email: undefined });
    };

    const handlePasswordChange = (value: string) => {
        setCredentials({ ...credentials, password: value });
        setFieldErrors({ ...fieldErrors, password: undefined });
    };

    const handleKeepSignedInChange = (keepSignedIn: boolean) => {
        setCredentials({ ...credentials, keepSignedIn });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Browser autofill can fill the inputs without telling React, so read what is on screen.
        const form = new FormData(event.currentTarget);
        const submitted: IAuthCredentials = {
            ...credentials,
            email: String(form.get('email') ?? credentials.email).trim(),
            password: String(form.get('password') ?? credentials.password),
        };
        setCredentials(submitted);

        const errors = validateCredentials(submitted);
        setFieldErrors(errors);
        if (errors.email || errors.password) {
            return;
        }

        await queryClient.resetQueries();

        loginMutation.mutate(submitted, {
            onSuccess: (user) => {
                if (!(RoleUtils.hasSupportRole(user) || RoleUtils.hasSalesRole(user))) {
                    void restaurantsQuery.refetch();
                }
            },
        });
    };

    const sendEmailVerificationMutation = useSendEmailVerificationMutation();

    const isLoading = loginMutation.isLoading || logoutMutation.isLoading || revokeAccountAccessMutation.isLoading;

    const ErrorMessage = () => {
        if (loginMutation.isError) {
            return <Label type="text" value={t('login.errors.invalidCredentials')} error />;
        }
        if (hasNoRestaurants || (restaurantsQuery.isError && restaurantsQuery.error?.status === 403)) {
            return <Label type="text" value={t('login.errors.noRestaurants')} error />;
        }
        if (restaurantsQuery.isError) {
            return <Label type="text" value={t('login.errors.somethingWentWrong')} error />;
        }
        if (authUserQuery.isSuccess && !authUserQuery.data.isLoggedIn) {
            return (
                <Label
                    type="text"
                    value={t('login.errors.pendingVerification')}
                    error
                    onClick={sendEmailVerificationMutation.mutate}
                />
            );
        }
        return null;
    };

    return (
        <AuthLayout name="login" title={t('login.title')} subtitle={t('login.subtitle')} isLoading={isLoading}>
            <form className="auth-layout__fields" noValidate onSubmit={handleSubmit}>
                <TextField
                    label={t('login.fields.email')}
                    name="email"
                    autoComplete="username"
                    type="email"
                    shrink
                    validate={false}
                    startAdornment={<EmailIcon />}
                    value={credentials.email}
                    onChange={handleEmailChange}
                    hasError={Boolean(fieldErrors.email)}
                    errorMessageKey={fieldErrors.email}
                />
                <TextField
                    label={t('login.fields.password')}
                    name="password"
                    autoComplete="current-password"
                    type={showPassword ? 'text' : 'password'}
                    shrink
                    validate={false}
                    startAdornment={<LockIcon />}
                    endAdornment={
                        <PasswordToggle visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                    }
                    value={credentials.password}
                    onChange={handlePasswordChange}
                    hasError={Boolean(fieldErrors.password)}
                    errorMessageKey={fieldErrors.password}
                />
                <div className="auth-layout__options">
                    <Switch
                        label={t('login.keepSignedIn')}
                        checked={credentials.keepSignedIn}
                        onChange={handleKeepSignedInChange}
                    />
                    <Link to={Path.RESET_PASSWORD} className="auth-layout__link">
                        {t('login.buttons.forgotPassword')}
                    </Link>
                </div>
                <ErrorMessage />
                <Button type="submit" label={t('login.buttons.login')} />
            </form>
            <div className="auth-layout__divider">{t('login.noAccount')}</div>
            <div className="auth-layout__card">
                <div className="auth-layout__card-title">{t('login.newTitle')}</div>
                <p className="auth-layout__card-text">{t('login.newText')}</p>
                <Button secondary label={t('login.buttons.createAccount')} onClick={() => navigate(Path.REGISTER)} />
            </div>
        </AuthLayout>
    );
};

export default Login;
